<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Annual Overhead Expenses</h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Annual overhead expenses are the costs associated with running your organization's day-to-day operations and supporting its overall infrastructure. While overhead expenses do not directly fund program activities, they are necessary to support the organization's overall functioning, governance, and sustainability.</p>
            
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Examples of overhead expenses include salaries and benefits for administrative staff (e.g., executive directors, finance or development personnel, human resources), rent or mortgage for office space, utilities, office supplies, insurance, accounting and legal services, IT support, marketing and communications expenses, and fundraising costs. If your organization is receiving in-kind goods or services, those costs should be accounted for in your overhead expense budget.</p>

            <p class="text-[14px] leading-[22px] mb-[1.5rem]">Excluding your program expenses, which you entered in the previous module, enter the estimated costs to cover your annual overhead expenses over the next three (3) years.</p>

            <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
                <ActivityIndicator />
            </div>

            <!-- Budget Table -->
            <div v-else>
                <BudgetTable 
                    :categories="expenseCategories"
                    :total-programs-budget="total_overhead_budget"
                    @delete-item="handleDeleteItem"
                    @data-changed="handleDataChanged"
                />
            </div>
        </div>
    </div>

    <!-- Confirmation Popup -->
    <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p class="text-gray-600 mb-6">Are you sure you want to delete this item?</p>
            <div class="flex justify-end space-x-4">
                <button
                    @click="onCancelRemove"
                    class="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                    Cancel
                </button>
                <button
                    @click="onConformRemove"
                    class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Delete
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import BudgetTable from '@/components/BudgetTable.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useFetchCache } from '@/composables/fetchCache';
import global from "@/global";

const authStore = useAuthStore();

// Use the validation composable with default settings
useStepValidation();

const isLoading = ref(true);
const total_overhead_budget = ref(0);
const conformationPopupIsOpen = ref(false);
const deletionFunction = ref(null);
const expenseCategories = ref([]);
const isSaving = ref(false);

// Add auto-save debounce timer
let autoSaveTimer = null;
const AUTO_SAVE_DELAY = 2000; // 2 seconds delay

const EXPENSE_TYPES = [
  'Personnel and Benefits',
  'Finance Fees',
  'Legal Fees',
  'Printing and Publications',
  'Travel, Conferences, and Meetings',
  'Marketing and Communications',
  'Other Expenses',
  'In-Kind Goods & Services'
];

// Add this mapping at the top, after EXPENSE_TYPES
const API_CATEGORY_KEY_MAP = {
  'Personnel and Benefits': 'personnel_and_benefits',
  'Finance Fees': 'finance_fees',
  'Legal Fees': 'legal_fees',
  'Printing and Publications': 'printing_and_publications',
  'Travel, Conferences, and Meetings': 'travel_conferences_and_meetings',
  'Marketing and Communications': 'marketing_and_communications',
  'Other Expenses': 'other_expenses',
  'In-Kind Goods & Services': 'in-kind_goods_services',
};
const API_CATEGORY_DISPLAY_MAP = Object.fromEntries(
  Object.entries(API_CATEGORY_KEY_MAP).map(([display, key]) => [key, display])
);

// API Integration using composables
const { error: fetchError, fetchCachedData, cachedData } = useFetchCache({
  uri: "/questions/annual_overhead_expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
});


// Initialize data
onMounted(async () => {
  try {
    // Fetch question data
    await fetchCachedData();
    localStorage.setItem(
      "questionFetchData",
      JSON.stringify(cachedData.value.question)
    );

    // Initialize expense categories
    expenseCategories.value = EXPENSE_TYPES.map(type => ({
      name: type,
      isOpen: type === 'Personnel and Benefits',
      items: []
    }));

    // Fetch overhead expenses - only those with null expenseable_type and expenseable_id
    try {
      // Use the dedicated endpoint for organization expenses
      const response = await fetch(`${global.API_URL}/organization-expenses`, {
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });
      const data = await response.json();
      
      // Map expenses to categories
      if (data && data.expenses) {
        expenseCategories.value = EXPENSE_TYPES.map(type => {
          const categoryKey = API_CATEGORY_KEY_MAP[type];
          let categoryExpenses = data.expenses[categoryKey] || [];
          
          return {
            name: type,
            isOpen: type === 'Personnel and Benefits',
            items: categoryExpenses.map(expense => ({
              id: expense.id,
              name: expense.expense_description,
              year1: Number(expense.expense_cost),
              year2: Number(expense.expense_cost_year_2),
              year3: Number(expense.expense_cost_year_3)
            }))
          };
        });

        // Calculate total overhead budget if available
        if (data.total) {
          total_overhead_budget.value = Number(data.total);
        }
      }
    } catch (error) {
      console.error('Error fetching overhead expenses:', error);
      // Continue with empty categories
    }

    isLoading.value = false;
  } catch (error) {
    console.error('Error loading data:', error);
    isLoading.value = false;
  }
});

// Cleanup on unmount
onUnmounted(() => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
});

// Delete confirmation popup handlers
function onCancelRemove() {
  conformationPopupIsOpen.value = false;
}

async function onConformRemove() {
  conformationPopupIsOpen.value = false;
  if (deletionFunction.value) {
    await deletionFunction.value();
    deletionFunction.value = null;
  }
}

// Delete expense
const deleteExpense = async (expenseId) => {
  try {
    const response = await fetch(`${global.API_URL}/organization-expenses/${expenseId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const data = await response.json();
    return data?.status || false;
  } catch (error) {
    console.error('Error deleting expense:', error);
    return false;
  }
};

// Save expenses data
const saveExpenses = async () => {
  if (isSaving.value) {
    return;
  }

  try {
    isSaving.value = true;
    
    // Filter out empty items (no name or all costs are 0)
    const allExpensesToSave = expenseCategories.value.flatMap(category => 
      category.items
        .filter(item => item.name && item.name.trim() !== '') // Only save items with names
        .map(item => ({
          id: item.id,
          expense_type: category.name,
          expense_description: item.name.trim(),
          expense_cost: item.year1.toString().replace(/,/g, ""),
          expense_cost_year_2: item.year2.toString().replace(/,/g, ""),
          expense_cost_year_3: item.year3.toString().replace(/,/g, "")
          // No need to explicitly set expenseable_type and expenseable_id to null
          // The backend will handle that for the organization-expenses endpoint
        }))
    );
    
    // Don't save if no expenses to save
    if (allExpensesToSave.length === 0) {
      return { status: true, expenses: {} };
    }
    
    // Make API call to save all expenses using the dedicated endpoint
    const response = await fetch(`${global.API_URL}/organization-expenses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
      body: JSON.stringify({
        expenses: allExpensesToSave,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save overhead expenses');
    }

    const data = await response.json();
    
    // If save was successful and we have new items without IDs, update IDs without reordering
    if (data.status) {
      await updateItemIdsWithoutReordering();
    }

    return data;
  } catch (error) {
    console.error("Error saving overhead expenses:", error);
    throw error;
  } finally {
    isSaving.value = false;
  }
};

// Event handlers
const handleDeleteItem = async (categoryIndex, itemIndex) => {
  const item = expenseCategories.value[categoryIndex].items[itemIndex];
  
  if (item.id) {
    conformationPopupIsOpen.value = true;
    deletionFunction.value = async () => {
      const success = await deleteExpense(item.id);
      if (success) {
        expenseCategories.value[categoryIndex].items.splice(itemIndex, 1);
      } else {
        console.error('Failed to delete expense');
      }
    };
  } else {
    expenseCategories.value[categoryIndex].items.splice(itemIndex, 1);
  }
};

// Handle data changes from BudgetTable for auto-save
const handleDataChanged = async (immediate = false) => {
  try {
    // Don't trigger auto-save if we're already saving
    if (isSaving.value) {
      return;
    }
    
    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    
    if (immediate) {
      // Save immediately
      try {
        await saveExpenses();
      } catch (error) {
        console.error('Immediate save failed:', error);
      }
    } else {
      // Set new timer for debounced save
      autoSaveTimer = setTimeout(async () => {
        try {
          // Trigger auto-save
          await saveExpenses();
        } catch (error) {
          console.error('Auto-save failed:', error);
          // Don't show error to user for auto-save failures to avoid interrupting their workflow
          // The error will be shown when they try to validate the step
        }
      }, AUTO_SAVE_DELAY);
    }
  } catch (error) {
    console.error('Error setting up auto-save:', error);
  }
};

// Update item IDs without changing order
const updateItemIds = (savedExpenses) => {
  // Create a map of expense descriptions to IDs for quick lookup
  const expenseMap = {};
  Object.values(savedExpenses).flat().forEach(expense => {
    const key = `${expense.expense_type}|${expense.expense_description}`;
    expenseMap[key] = expense.id;
  });
  
  // Update IDs in expense categories without changing order
  expenseCategories.value.forEach(category => {
    category.items.forEach(item => {
      if (!item.id) { // Only update items that don't have an ID yet
        const key = `${category.name}|${item.name}`;
        if (expenseMap[key]) {
          item.id = expenseMap[key];
        }
      }
    });
  });
};

// Add method to update item IDs without reordering
const updateItemIdsWithoutReordering = async () => {
  try {
    // Check if we have any items without IDs
    const hasNewItems = expenseCategories.value.some(category => 
      category.items.some(item => !item.id && item.name && item.name.trim() !== '')
    );
    
    if (!hasNewItems) {
      return; // No new items to update
    }
    
    // Fetch the latest expenses to get new IDs
    const response = await fetch(`${global.API_URL}/organization-expenses`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    
    // Create a map of expense descriptions to IDs for quick lookup
    const expenseMap = {};
    Object.values(data.expenses).flat().forEach(expense => {
      const key = `${expense.expense_type}|${expense.expense_description}`;
      expenseMap[key] = expense.id;
    });
    
    // Update IDs in categories without changing order
    let updatedCount = 0;
    expenseCategories.value.forEach(category => {
      category.items.forEach(item => {
        if (!item.id && item.name && item.name.trim() !== '') { // Only update items that don't have an ID yet
          const key = `${category.name}|${item.name.trim()}`;
          if (expenseMap[key]) {
            item.id = expenseMap[key];
            updatedCount++;
          }
        }
      });
    });
    
    // Only update total budget if we actually updated some items
    if (updatedCount > 0 && data.total) {
      total_overhead_budget.value = Number(data.total);
    }
  } catch (error) {
    console.error('Error updating item IDs:', error);
    // Don't throw error to avoid breaking the save flow
  }
};

// Expose saveExpenses to parent component
defineExpose({
  saveExpenses,
  handleDeleteItem
});
</script>

<style scoped>
</style> 